import { useRef, useState } from "react";
import useVoiceLoader from "./useVoiceLoader";
import useAudioUnlock from "./useAudioUnlock";

const SYNTHESIS_CONFIG = {
    PITCH: 1.1,
    RATE: 1.1,
    VOLUME: 1
}

const adjustStartPosition = (text, startPosition) => {
    let adjustedPosition = Math.max(0, Math.min(startPosition, text.length - 1));
    const previousChar = text.charAt(adjustedPosition - 1);
    if (!/[\s,.!?;:]/.test(previousChar)) {
        const lastSpace = text.lastIndexOf(" ", adjustedPosition - 1);
        if (lastSpace !== -1) adjustedPosition = lastSpace + 1;
    }
    return adjustedPosition;
};


const useSpeechSynthesis = (onSelect) => {
    const [isSpeaking, setIsSpeaking] = useState(false);
    const [isPaused, setIsPaused] = useState(false);
    const [voices, setVoices] = useState([]);
    const [selectedVoice, setSelectedVoice] = useState(null);
    const [currentText, setCurrentText] = useState("");
    const [speechPosition, setSpeechPosition] = useState(0);
    const speechTimer = useRef(null);

    useVoiceLoader(setVoices, setSelectedVoice);
    useAudioUnlock();

    const startPositionTracking = (initialPosition = 0) => {
        if (speechTimer.current) clearInterval(speechTimer.current);
        const validPosition = Math.max(0, Math.min(initialPosition, currentText.length));
        setSpeechPosition(validPosition);
        const startTime = Date.now();
        const charsPerSecond = 12;

        speechTimer.current = setInterval(() => {
            const elapsed = Date.now() - startTime;
            const progressedChars = (elapsed / 1000) * charsPerSecond;
            const estimated = Math.min(Math.floor(validPosition + progressedChars), currentText.length);
            setSpeechPosition(estimated);
        }, 100);
    };

    const stopPositionTracking = () => {
        if (speechTimer.current) {
            clearInterval(speechTimer.current);
            speechTimer.current = null;
        }
    };

    const speak = (text, startPosition = 0) => {
        if (!text?.trim()) return;

        const synthesis = window.speechSynthesis;
        synthesis.cancel();
        setCurrentText(text);

        const adjustedStart = adjustStartPosition(text, startPosition);
        const speakText = text.substring(adjustedStart);

        const expressiveText = speakText
            .replace(/!/g, ".")
            .replace(/\.\.\./g, ",,,")
            .replace(/\?/g, "?");

        const utterance = new SpeechSynthesisUtterance(expressiveText);
        if (selectedVoice) utterance.voice = selectedVoice;
        utterance.pitch = SYNTHESIS_CONFIG.PITCH;
        utterance.rate = SYNTHESIS_CONFIG.RATE;
        utterance.volume = SYNTHESIS_CONFIG.VOLUME;

        utterance.onstart = () => {
            setIsSpeaking(true);
            setIsPaused(false);
            startPositionTracking(adjustedStart);
        };

        utterance.onend = () => {
            setIsSpeaking(false);
            setIsPaused(false);
            stopPositionTracking();
            setSpeechPosition(0);
            onSelect(null);
        };

        utterance.onerror = (e) => {
            setIsSpeaking(false);
            setIsPaused(false);
            stopPositionTracking();
            onSelect(null);
        };

        synthesis.speak(utterance);

        if (synthesis.pending) {
            synthesis.pause();
            synthesis.resume();
        }
    };

    const pause = () => {
        const synthesis = window.speechSynthesis;
        if (synthesis.speaking) {
            const position = speechPosition;
            stopPositionTracking();
            synthesis.pause();
            setIsPaused(true);
            setSpeechPosition(position);
            return true;
        }
        return false;
    };

    const resume = () => {
        const synthesis = window.speechSynthesis;
        if (isPaused) {
            const position = speechPosition;
            synthesis.cancel();
            speak(currentText, position);
            setIsPaused(false);
            return true;
        }
        return false;
    };

    const stop = () => {
        window.speechSynthesis.cancel();
        setIsSpeaking(false);
        setIsPaused(false);
        stopPositionTracking();
        setSpeechPosition(0);
        setCurrentText("");
    };

    const changeVoice = (voice) => {
        setSelectedVoice(voice);
        if (isSpeaking) {
            const text = currentText;
            stop();
            requestAnimationFrame(() => speak(text));
        }
    };

    return {
        speak,
        pause,
        resume,
        stop,
        isSpeaking,
        isPaused,
        voices,
        selectedVoice,
        setSelectedVoice: changeVoice,
        currentText,
        speechPosition,
    };
};

export default useSpeechSynthesis;
